import React, { createContext, useState, useContext, useRef } from 'react';
import Toast from '../components/Toast';

// Create the context
const ToastContext = createContext();

/**
 * Toast Provider component to manage toast notifications
 */
export const ToastProvider = ({ children }) => {
  const [toast, setToast] = useState({
    visible: false,
    message: '',
    type: 'success',
    duration: 2000, // Default duration, will be calculated based on message length
  });
  
  // Use a ref to track the toast timers so we can clear them
  const toastTimers = useRef({ animation: null, close: null });

  // Function to show a toast
  const showToast = (message, type = 'success', customDuration = null) => {
    // Clear any existing timers to prevent the current toast from closing
    if (toastTimers.current.animation) {
      clearTimeout(toastTimers.current.animation);
    }
    if (toastTimers.current.close) {
      clearTimeout(toastTimers.current.close);
    }
    
    // Calculate duration based on message length if customDuration is not provided
    let duration = customDuration;
    if (duration === null) {
      // 1 second per 10 characters, with min 2 seconds and max 4 seconds
      const calculatedDuration = Math.min(
        4000, // Maximum 4 seconds
        Math.max(
          2000, // Minimum 2 seconds
          Math.ceil(message.length / 10) * 1000 // 1 second per 10 characters
        )
      );
      duration = calculatedDuration;
    }
    
    // Immediately show the new toast
    setToast({
      visible: true,
      message,
      type,
      duration,
    });
  };

  // Function to hide the toast
  const hideToast = () => {
    // Clear any existing timers
    if (toastTimers.current.animation) {
      clearTimeout(toastTimers.current.animation);
    }
    if (toastTimers.current.close) {
      clearTimeout(toastTimers.current.close);
    }
    
    setToast(prevToast => ({
      ...prevToast,
      visible: false,
    }));
  };

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <Toast
        message={toast.message}
        type={toast.type}
        visible={toast.visible}
        onClose={hideToast}
        duration={toast.duration}
        timersRef={toastTimers}
      />
    </ToastContext.Provider>
  );
};

/**
 * Custom hook to use the toast context
 */
export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export default ToastContext;